import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GitBranch, AlertTriangle, Info, ChevronDown, ChevronUp, Shield, Radio } from 'lucide-react';
import { api } from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';

const SEVERITY_CONFIG = {
  high: { label: 'High', bg: '#fef2f2', color: '#991b1b', border: '#fecaca', dot: '#dc2626' },
  medium: { label: 'Medium', bg: '#fffbeb', color: '#92400e', border: '#fde68a', dot: '#d97706' },
  low: { label: 'Low', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', dot: '#16a34a' },
};

const formatCurrency = (v) => {
  if (!v) return '-';
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toFixed(0);
};

const SourceConflictSection = ({ refreshKey }) => {
  const { theme } = useTheme();
  const c = colors[theme];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getSourceConflicts();
      setData(res.data);
    } catch (e) {
      console.error('Error loading source conflicts:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (refreshKey > 0) {
      api.getSourceConflicts()
        .then((res) => setData(res.data))
        .catch(() => {});
    }
  }, [refreshKey]);

  if (loading) {
    return (
      <div
        className="rounded-xl p-6 mb-4"
        style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
        data-testid="source-conflicts-loading"
      >
        <div className="flex items-center gap-2 mb-4">
          <GitBranch size={16} style={{ color: c.textSecondary }} />
          <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>Source Conflicts</span>
        </div>
        <div className="animate-pulse h-12 rounded-lg" style={{ background: c.background }} />
      </div>
    );
  }

  if (!data) return null;

  const { conflicts, summary, upstream_status, upstream_message, total } = data;
  const isConnected = upstream_status === 'connected';
  const hasConflicts = conflicts && conflicts.length > 0;

  return (
    <motion.div
      className="rounded-xl p-5 mb-4"
      style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="source-conflicts-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitBranch size={16} style={{ color: c.accentBlue }} />
          <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>
            Source Conflicts
          </span>
          {total > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background: '#fef2f2', color: '#991b1b' }}
            >
              {total}
            </span>
          )}
        </div>

        {/* Connection status */}
        <div className="flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: isConnected ? '#16a34a' : '#94a3b8' }}
          />
          <span className="text-[10px] font-mono" style={{ color: c.textSecondary }}>
            {isConnected ? 'Live' : upstream_status === 'not_configured' ? 'Not configured' : 'Pending'}
          </span>
        </div>
      </div>

      {/* ── Not connected / awaiting upstream ── */}
      {!isConnected && !hasConflicts && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-lg text-xs"
          style={{ background: c.background, color: c.textSecondary }}
          data-testid="source-conflicts-pending"
        >
          <Radio size={14} className="shrink-0 mt-0.5" style={{ color: c.textSecondary }} />
          <div>
            <p className="font-medium mb-1" style={{ color: c.textPrimary }}>
              Awaiting Sales Capture conflict feed
            </p>
            <p style={{ color: c.textSecondary }}>
              {upstream_message || 'This section will activate automatically once Sales Capture ships the source-conflicts endpoint. No manual configuration needed.'}
            </p>
            <p className="mt-2 text-[10px] font-mono" style={{ color: c.textSecondary }}>
              Sales Capture enforces single-active-source per outlet/date. Shadow data is excluded from downstream reporting.
            </p>
          </div>
        </div>
      )}

      {/* ── Connected, no conflicts ── */}
      {isConnected && !hasConflicts && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-xs"
          style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
          data-testid="source-conflicts-clear"
        >
          <Shield size={14} />
          <span className="font-medium">No source conflicts detected. All outlets have single-source sales data.</span>
        </div>
      )}

      {/* ── Connected, has conflicts ── */}
      {hasConflicts && (
        <>
          {/* Summary pills */}
          <div className="flex items-center gap-2 mb-3" data-testid="source-conflicts-summary">
            {['high', 'medium', 'low'].map((sev) => {
              const cfg = SEVERITY_CONFIG[sev];
              const count = summary[sev] || 0;
              if (count === 0) return null;
              return (
                <span
                  key={sev}
                  className="px-2 py-1 rounded-lg text-[10px] font-semibold"
                  style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
                >
                  {count} {cfg.label}
                </span>
              );
            })}
          </div>

          {/* Conflict list */}
          <div className="space-y-1.5" data-testid="source-conflicts-list">
            {conflicts.map((conflict, i) => {
              const cfg = SEVERITY_CONFIG[conflict.severity] || SEVERITY_CONFIG.low;
              const isExpanded = expandedIdx === i;
              const revDelta = conflict.primary_revenue && conflict.shadow_revenue
                ? Math.abs(conflict.primary_revenue - conflict.shadow_revenue)
                : null;

              return (
                <div key={`${conflict.outlet_id}-${conflict.business_date}-${i}`}>
                  <button
                    onClick={() => setExpandedIdx(isExpanded ? null : i)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-left transition-colors"
                    style={{ background: c.background }}
                    data-testid={`conflict-row-${i}`}
                  >
                    {/* Severity dot */}
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cfg.dot }} />

                    {/* Outlet + date */}
                    <span className="flex-1 font-medium truncate" style={{ color: c.textPrimary }}>
                      {conflict.outlet_name || conflict.outlet_id}
                    </span>
                    <span className="font-mono shrink-0" style={{ color: c.textSecondary }}>
                      {conflict.business_date}
                    </span>

                    {/* Primary source badge */}
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0"
                      style={{ background: c.accentGreen + '15', color: c.accentGreen }}
                    >
                      {(conflict.primary_source_type || '').replace(/_/g, ' ')}
                    </span>

                    {/* Shadow count */}
                    <span className="shrink-0" style={{ color: c.textSecondary }}>
                      +{conflict.shadow_source_types?.length || 0} shadow
                    </span>

                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div
                          className="mx-3 mt-1 mb-2 px-4 py-3 rounded-lg text-xs space-y-2"
                          style={{ background: c.background, border: `1px solid ${c.border}` }}
                          data-testid={`conflict-detail-${i}`}
                        >
                          {/* Operator copy */}
                          <p style={{ color: c.textSecondary }}>
                            Multiple sales sources were detected for this outlet/date.
                            Sales Capture selected <strong style={{ color: c.textPrimary }}>{(conflict.primary_source_type || '').replace(/_/g, ' ')}</strong> as
                            the primary source automatically. Shadow data is excluded from downstream reporting.
                          </p>

                          {/* Detail grid */}
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 pt-1">
                            <div>
                              <span style={{ color: c.textSecondary }}>Primary rows: </span>
                              <span className="font-mono font-medium" style={{ color: c.textPrimary }}>{conflict.primary_row_count}</span>
                            </div>
                            <div>
                              <span style={{ color: c.textSecondary }}>Shadow rows: </span>
                              <span className="font-mono font-medium" style={{ color: c.textPrimary }}>{conflict.shadow_row_count}</span>
                            </div>
                            <div>
                              <span style={{ color: c.textSecondary }}>Primary revenue: </span>
                              <span className="font-mono font-medium" style={{ color: c.textPrimary }}>{formatCurrency(conflict.primary_revenue)}</span>
                            </div>
                            <div>
                              <span style={{ color: c.textSecondary }}>Shadow revenue: </span>
                              <span className="font-mono font-medium" style={{ color: c.textPrimary }}>{formatCurrency(conflict.shadow_revenue)}</span>
                            </div>
                            {conflict.shadow_source_types?.length > 0 && (
                              <div className="col-span-2">
                                <span style={{ color: c.textSecondary }}>Shadowed sources: </span>
                                <span className="font-medium" style={{ color: c.textPrimary }}>
                                  {conflict.shadow_source_types.map((s) => s.replace(/_/g, ' ')).join(', ')}
                                </span>
                              </div>
                            )}
                            {revDelta !== null && revDelta > 0 && (
                              <div className="col-span-2">
                                <span style={{ color: c.textSecondary }}>Revenue delta: </span>
                                <span className="font-mono font-medium" style={{ color: '#d97706' }}>{formatCurrency(revDelta)}</span>
                              </div>
                            )}
                          </div>

                          {/* Review guidance */}
                          <p className="pt-1 text-[10px]" style={{ color: c.textSecondary }}>
                            Review only if the shadowed source indicates a process issue (e.g., manual upload after POS data already arrived).
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default SourceConflictSection;
